import { NavLink } from "react-router";

import styled from "styled-components";

import type { Server } from "../../types/server";

import ServerImage from "../../assets/server.png";
import UserImage from "../../assets/user.png";

import { ReactComponent as StarIcon } from "../../assets/icons/star.svg";
import { ReactComponent as DownloadingIcon } from "../../assets/icons/downloading.svg";

const Root = styled(NavLink)`
  cursor: pointer;
  height: auto;
  aspect-ratio: 430 / 240;
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease-in-out;
  &:hover {
    filter: brightness(1.1);
  }
  display: flex;
  flex-flow: column nowrap;
  body:has(&:hover) &:not(:hover) {
    filter: brightness(0.8);
    opacity: 0.8;
  }
`;

const Picture = styled.picture`
  width: 100%;
  position: relative;
  display: flex;
  aspect-ratio: 428/182;
  background-image: url(${ServerImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  & > span {
    height: 75%;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
    background: linear-gradient(
      to bottom,
      rgba(10, 10, 10, 0),
      rgba(10, 10, 10, 1)
    );
    display: block;
  }
`;

const Tag = styled.div`
  position: absolute;
  height: 30px;
  z-index: 10;
  top: 0;
  left: 0;
  width: 56px;
  text-transform: uppercase;
  background: #03fb73;
  color: #000;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  clip-path: polygon(
    0 0,
    /* top left */ 100% 0,
    /* top right */ 100% calc(100% - 12px),
    /* bottom right vertical */ calc(100% - 12px) 100%,
    /* bottom right horizontal */ 0 100% /* bottom left */
  );
`;
const Footer = styled.div`
  flex-grow: 1;
  background-color: #232323;
  height: auto;
  flex-shrink: 1;
  border-radius: 0 0 3px 3px;
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;
const User = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
  & > picture {
    height: 30px;
    width: 30px;
    margin: 2px;
    box-shadow: 0 0 0 2px #000;
    border-radius: 50%;
    flex-shrink: 0;
    background-image: url(${UserImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
  & > span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.56);
    font-weight: 600;
    line-height: 16px;
    text-transform: uppercase;
  }
`;
const Download = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
  height: 32px;
  border-radius: 3px;
  padding: 0 12px;
  clip-path: polygon(
    0 0,
    /* top left */ 100% 0,
    /* top right */ 100% calc(100% - 15px),
    /* bottom right vertical */ calc(100% - 15px) 100%,
    /* bottom right horizontal */ 0 100% /* bottom left */
  );
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
`;
const Details = styled.div`
  padding-left: 14px;
  padding-bottom: 14px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: flex-start;
  z-index: 2;
  width: 100%;
  position: absolute;
  bottom: 0;
  gap: 10px;
  left: 0;
  & > h3 {
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;
    color: #fff;
    text-transform: uppercase;
  }
  & > div {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 8px;
  }
`;
const DetailGroup = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.56);
  text-transform: uppercase;
  & > span {
    font-size: 12px;
    line-height: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.56);
  }
`;
const Divider = styled.div`
  flex-shrink: 0;
  width: 1px;
  height: 12px;
  background-color: rgba(255, 255, 255, 0.32);
  display: block;
`;
const Star = styled(StarIcon)`
  height: 20px;
  width: 20px;
  flex-shrink: 0;
  margin-right: -2px;
  color: #ffca00;
`;
const Downloading = styled(DownloadingIcon)`
  height: 20px;
  width: 20px;
  flex-shrink: 0;
`;

export default function ServerView({ server }: { server: Server }) {
  return (
    <Root to={`/servers/${server.id}`}>
      <Picture>
        <span />
        <Tag>New</Tag>
        <Details>
          <h3>{server.name}</h3>
          <div>
            <DetailGroup style={{ color: "#fff" }}>
              <Star />
              4.8<span>(120)</span>
            </DetailGroup>
            <Divider />
            <DetailGroup>
              <Downloading />
              9.8k
            </DetailGroup>
          </div>
        </Details>
      </Picture>
      <Footer>
        <User>
          <picture />
          <span>by conqr</span>
        </User>
        <Download>1.2GB</Download>
      </Footer>
    </Root>
  );
}
